/**
 * S-03 月間一覧入力。Excel に近い一覧性で、日付ごとに横へ入力していく画面。
 *
 * 変更した行だけをまとめて保存する。保存前でも計算結果はブラウザ側で更新する。
 */

import {
  computeDaily,
  DEFAULT_WEEKDAY_BREAK_MINUTES,
  WORK_TYPES,
  type DailyInput,
  type WorkType,
} from '@meisui/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import type { MonthlyDay } from '../../api/types.js';
import { TimeField } from '../../components/TimeField.js';
import { useTimeDrafts } from '../../lib/useTimeDrafts.js';
import { useGuardedAction, useUnsavedChanges } from '../../lib/unsaved-context.js';
import { MonthNav } from '../../components/MonthNav.js';
import { Card, ErrorMessage, Loading, Notice, StatusPill } from '../../components/ui.js';
import { currentMonth, formatDuration, todayIso } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

type Row = Omit<DailyInput, 'date' | 'dayType'>;
type Columns = 'all' | 'times' | 'computed';

function toRow(day: MonthlyDay): Row {
  const report = day.report;
  if (!report) {
    return {
      workType: day.dayType === '平日' ? '出勤' : '休日出勤',
      directStart: null,
      officeIn: null,
      workStart: null,
      workEnd: null,
      officeOut: null,
      directHome: null,
      breakTotalMinutes: day.dayType === '平日' ? DEFAULT_WEEKDAY_BREAK_MINUTES : null,
      hourlyLeaveHours: 0,
      breakOverrideMinutes: null,
      holidayWorkTreatment: null,
      siteName: '',
      taskNote: '',
      remarks: '',
    };
  }
  return {
    workType: report.workType,
    directStart: report.directStart,
    officeIn: report.officeIn,
    workStart: report.workStart,
    workEnd: report.workEnd,
    officeOut: report.officeOut,
    directHome: report.directHome,
    breakTotalMinutes: report.breakTotalMinutes,
    hourlyLeaveHours: report.hourlyLeaveHours,
    breakOverrideMinutes: report.breakOverrideMinutes,
    holidayWorkTreatment: report.holidayWorkTreatment,
    siteName: report.siteName,
    taskNote: report.taskNote,
    remarks: report.remarks,
  };
}

const sameRow = (a: Row, b: Row): boolean => JSON.stringify(a) === JSON.stringify(b);

export function MonthlyEntryPage(): React.JSX.Element {
  const times = useTimeDrafts();
  const requestAction = useGuardedAction();
  const savingRef = useRef(false);
  const [month, setMonth] = useState(currentMonth());
  const state = useAsync(() => endpoints.monthlyReports(month), [month]);

  const [rows, setRows] = useState<Record<string, Row>>({});
  const [original, setOriginal] = useState<Record<string, Row>>({});
  /** 読み込んだ時点の版番号。保存時に送り、別の端末の更新を上書きしないようにする */
  const [revisions, setRevisions] = useState<Record<string, number | null>>({});
  const [columns, setColumns] = useState<Columns>('all');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);

  useEffect(() => {
    if (!state.data) return;
    const next: Record<string, Row> = {};
    const nextRevisions: Record<string, number | null> = {};
    for (const day of state.data.days) {
      next[day.date] = toRow(day);
      nextRevisions[day.date] = day.report?.revision ?? null;
    }
    times.reset();
    setRows(next);
    setOriginal(next);
    setRevisions(nextRevisions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data]);

  // 保存後の読み直しで「N日分を保存しました」が消えないよう、知らせの初期化は月が変わったときだけ行う
  useEffect(() => {
    setMessage(null);
    setSaveError(null);
  }, [month]);

  const changedDates = useMemo(
    () =>
      Object.keys(rows).filter((date) => original[date] && !sameRow(rows[date]!, original[date]!)),
    [rows, original],
  );

  // 未入力の警告は「入力が必要な平日」に限る（休日は勤務した日だけ入力する）
  const today = todayIso();
  const days = state.data?.days ?? [];
  const targetDays = days.filter((day) => day.dayType === '平日' && day.date <= today);
  const enteredDays = targetDays.filter((day) => day.report !== null);
  const draftDays = days.filter((day) => day.report?.status === 'draft');

  const visibleDays = onlyMissing ? days.filter((day) => day.report === null) : days;
  const closed = state.data?.closed ?? false;

  const update = (date: string, patch: Partial<Row>): void => {
    setRows((previous) => ({ ...previous, [date]: { ...previous[date]!, ...patch } }));
    setMessage(null);
  };

  const copyPrevious = (date: string): void => {
    const index = days.findIndex((day) => day.date === date);
    const previousDay = days[index - 1];
    if (!previousDay) return;
    const source = rows[previousDay.date];
    if (!source) return;
    times.clear(
      ['directStart', 'officeIn', 'workStart', 'workEnd', 'officeOut', 'directHome'].map(
        (field) => `${date}:${field}`,
      ),
    );
    update(date, {
      directStart: source.directStart,
      officeIn: source.officeIn,
      workStart: source.workStart,
      workEnd: source.workEnd,
      officeOut: source.officeOut,
      directHome: source.directHome,
      breakTotalMinutes: source.breakTotalMinutes,
    });
  };

  const save = async (): Promise<boolean> => {
    // 月の切替直後は前の月の内容が残っている。読み込みが終わるまで保存しない
    if (state.data?.month !== month || savingRef.current || closed) return false;
    if (times.hasErrors) {
      setSaveError(new Error('時刻の入力形式を修正してから保存してください。'));
      return false;
    }
    if (changedDates.length === 0) return true;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await endpoints.bulkSave(
        changedDates.map((date) => ({
          date,
          ...rows[date]!,
          revision: revisions[date] ?? null,
        })),
      );
      setMessage(`${changedDates.length}日分を保存しました。`);
      setOriginal(rows);
      setRevisions((previous) => ({
        ...previous,
        ...Object.fromEntries(result.days.map((day) => [day.report.date, day.report.revision])),
      }));
      state.reload();
      return true;
    } catch (cause) {
      setSaveError(cause);
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const dirty = changedDates.length > 0 || times.hasErrors;
  useUnsavedChanges({ dirty, saving, save });

  return (
    <>
      <MonthNav month={month} onChange={(next) => requestAction(() => setMonth(next))} />

      <Notice tone="info">
        画面を横にスクロールして入力します。PC・タブレットでの利用を推奨します。
      </Notice>

      {state.error !== null && <ErrorMessage error={state.error} />}
      <ErrorMessage error={saveError} />
      {message !== null && <Notice tone="info">{message}</Notice>}
      {times.hasErrors && (
        <Notice tone="danger">
          時刻の入力形式を確認してください。「すべて表示」で入力欄を確認できます。修正するまで保存されません。
        </Notice>
      )}
      {saving && (
        <p role="status" className="text-sm">
          保存中です…
        </p>
      )}
      {closed && (
        <Notice tone="info" title="この月は確定済みです">
          内容は編集できません。
        </Notice>
      )}

      {state.loading && !state.data && <Loading />}

      {state.data && (
        <Card
          title="入力状況"
          description={`対象 ${targetDays.length}日／入力済み ${enteredDays.length}日／下書き ${draftDays.length}日`}
          action={
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="input w-auto"
                value={columns}
                onChange={(event) => setColumns(event.target.value as Columns)}
                aria-label="表示項目"
              >
                <option value="all">すべて表示</option>
                <option value="times">勤務時刻のみ</option>
                <option value="computed">自動計算のみ</option>
              </select>
              <label className="flex items-center gap-1 text-[0.8rem] text-ink-sub">
                <input
                  type="checkbox"
                  checked={onlyMissing}
                  onChange={(event) => setOnlyMissing(event.target.checked)}
                />
                未入力だけ表示
              </label>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={saving || state.loading || closed || !dirty}
                onClick={() => void save()}
              >
                {changedDates.length > 0
                  ? `${changedDates.length}日分を保存`
                  : 'すべて保存されています'}
              </button>
              {dirty && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={saving || state.loading}
                  onClick={() => {
                    setRows(original);
                    times.reset();
                  }}
                >
                  変更を取り消す
                </button>
              )}
            </div>
          }
        >
          {dirty && (
            <p className="mb-2 text-[0.8rem] text-warn-600">保存していない変更があります。</p>
          )}

          <div className="table-wrap max-h-[70vh]">
            <table className="table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 z-20 bg-canvas">日付</th>
                  {columns !== 'computed' && (
                    <>
                      <th>勤務種別</th>
                      <th>①直行</th>
                      <th>②出社</th>
                      <th>③始業</th>
                      <th>④終業</th>
                      <th>⑤退社</th>
                      <th>⑥直帰</th>
                      <th>⑦休憩</th>
                      <th>⑧年休</th>
                    </>
                  )}
                  {columns === 'all' && <th>主な業務</th>}
                  {columns !== 'times' && (
                    <>
                      <th className="whitespace-nowrap text-right">実働</th>
                      <th className="whitespace-nowrap text-right">移動</th>
                      <th className="whitespace-nowrap text-right">稼働</th>
                    </>
                  )}
                  <th>状態</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibleDays.map((day) => {
                  const row = rows[day.date];
                  if (!row) return null;
                  const computed = computeDaily({ ...row, date: day.date, dayType: day.dayType });
                  // 提出済み・承認済み・確定済みは一覧からも編集しない。提出済みは1日ずつ入力で取り消してから直す
                  const editable =
                    !saving &&
                    !state.loading &&
                    !closed &&
                    !(day.report?.locked ?? false) &&
                    day.report?.status !== 'approved' &&
                    day.report?.status !== 'submitted';
                  const changed = original[day.date] && !sameRow(row, original[day.date]!);

                  return (
                    <tr key={day.date} className={changed ? 'bg-brand-50' : undefined}>
                      <td
                        className={`sticky left-0 z-10 whitespace-nowrap font-semibold ${
                          changed ? 'bg-brand-50' : 'bg-white'
                        }`}
                      >
                        {Number(day.date.slice(8))}日（{day.weekday}）
                        {day.dayType !== '平日' && (
                          <span className="ml-1 pill pill--info">{day.dayType}</span>
                        )}
                      </td>

                      {columns !== 'computed' && (
                        <>
                          <td>
                            <select
                              className="input min-w-24"
                              value={row.workType}
                              disabled={!editable}
                              onChange={(event) =>
                                update(day.date, { workType: event.target.value as WorkType })
                              }
                            >
                              {WORK_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          </td>
                          {(
                            [
                              'directStart',
                              'officeIn',
                              'workStart',
                              'workEnd',
                              'officeOut',
                              'directHome',
                            ] as const
                          ).map((field) => (
                            <td key={field}>
                              <TimeField
                                compact
                                label={`${day.date} ${TIME_LABELS[field]}`}
                                disabled={!editable}
                                {...times.bind(`${day.date}:${field}`, row[field], (value) =>
                                  update(day.date, { [field]: value }),
                                )}
                              />
                            </td>
                          ))}
                          <td>
                            <input
                              className="input w-20"
                              type="number"
                              min={0}
                              value={row.breakTotalMinutes ?? ''}
                              disabled={!editable}
                              onChange={(event) =>
                                update(day.date, {
                                  breakTotalMinutes:
                                    event.target.value === '' ? null : Number(event.target.value),
                                })
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="input w-16"
                              type="number"
                              min={0}
                              max={8}
                              value={row.hourlyLeaveHours}
                              disabled={!editable}
                              onChange={(event) =>
                                update(day.date, { hourlyLeaveHours: Number(event.target.value) })
                              }
                            />
                          </td>
                        </>
                      )}

                      {columns === 'all' && (
                        <td>
                          <input
                            className="input min-w-40"
                            type="text"
                            value={row.taskNote}
                            disabled={!editable}
                            onChange={(event) => update(day.date, { taskNote: event.target.value })}
                          />
                        </td>
                      )}

                      {columns !== 'times' && (
                        <>
                          {/* 「8時間00分」が文字ごとに折り返されて行が伸びないよう、1行で出す */}
                          <td className="whitespace-nowrap text-right">
                            {formatDuration(computed.workMinutes)}
                          </td>
                          <td className="whitespace-nowrap text-right">
                            {formatDuration(computed.travelMinutes)}
                          </td>
                          <td className="whitespace-nowrap text-right">
                            {formatDuration(computed.totalMinutes)}
                          </td>
                        </>
                      )}

                      <td>
                        <StatusPill
                          status={day.report?.status ?? null}
                          locked={day.report?.locked}
                        />
                      </td>
                      <td className="whitespace-nowrap">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={!editable}
                          onClick={() => copyPrevious(day.date)}
                        >
                          前日コピー
                        </button>
                        <Link className="btn btn--ghost btn--sm" to={`/daily/${day.date}`}>
                          個別入力
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

const TIME_LABELS = {
  directStart: '①直行',
  officeIn: '②出社',
  workStart: '③始業',
  workEnd: '④終業',
  officeOut: '⑤退社',
  directHome: '⑥直帰',
};
